import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { OrganizationStrip } from 'src/components/reservations/organization-strip'
import { numberQueries } from 'src/services/numbers'
import { cn } from 'src/utils/style'
import type { OrganizationShape } from 'src/services/user/models'
import type { Membership } from 'src/services/user'

interface ChatOrgSelectorProps {
  /** The user's memberships — the org list needs no fetch. */
  memberships: Membership[]
  selectedOrgId: string | null
  onSelect: (orgId: string) => void
}

/**
 * Card strip that makes the org the chat is scoped to explicit, and shows at a
 * glance which orgs can actually send WhatsApp. Reuses the reservations
 * `OrganizationStrip` so chat and reservations share one card pattern. The org
 * list comes straight from the user's memberships (no fetch); the per-org
 * sendable-number status comes from a single summary read (not one request per
 * card), kept live by the query's refetch so a number that finishes
 * provisioning updates its card without a reload.
 */
export const ChatOrgSelector: FC<ChatOrgSelectorProps> = ({
  memberships,
  selectedOrgId,
  onSelect,
}) => {
  const { t } = useTranslation()

  // Memberships carry the org identity already; map them onto the shape the
  // strip renders. Only the id and name are used for selection.
  const organizations = useMemo<OrganizationShape[]>(
    () =>
      memberships.map((m) => ({
        id: m.organizationId,
        name: m.organizationName,
        defaultCurrency: '',
      })),
    [memberships],
  )

  // One summary read for every org the user belongs to — no per-card request.
  const { data: summary } = useQuery(numberQueries.sendableSummary())
  const sendableByOrg = useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const row of summary ?? []) map[row.organization] = row.hasSendableNumber
    return map
  }, [summary])

  if (organizations.length === 0) return null

  return (
    <section data-testid="chat-org-selector">
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-placeholder">
        {t('reservations.organizationsLabel')}
      </h2>
      <OrganizationStrip
        organizations={organizations}
        selectedOrgId={selectedOrgId}
        onSelect={onSelect}
        isLoading={false}
        testIdPrefix="chat-org-strip"
        renderBadge={(org) => {
          const canSend = sendableByOrg[org.id] ?? false
          return (
            <span
              data-testid={`chat-org-status-${org.id}`}
              data-can-send={canSend}
              className={cn([
                'inline-flex items-center gap-1 self-start rounded-full px-2 py-0.5 text-xs',
                canSend ? 'bg-green-50 text-green' : 'bg-gray-100 text-text-secondary',
              ])}
            >
              {canSend ? (
                <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ExclamationCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {canSend ? t('chat.orgCanSend') : t('chat.orgNoSendableNumber')}
            </span>
          )
        }}
      />
    </section>
  )
}
